import { useLoadScript } from "@react-google-maps/api";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";



import ComponentCircleLoader from "../../components/Loaders/ComponentCircleLoader";
import { useScrollShadow } from "../../customHooks/useScrollShadow";
import { CommonFlex, CommonGrid, ContainerNew, PageTitle } from "../../styledComponents/common";
import { StickyFilterContainer } from "../../styledComponents/timesheet";
import JobListInMapContainer from "./JobListInMapContainer/JobListInMapContainer";
import JobsInGoogleMap from "./JobsInGoogleMap/JobsInGoogleMap";
import JobsInMapFilter from "./JobsInMapFilter/jobsInMapFilter";
import { MapAndAlertContainer, MapTopAlert } from "./jobsInMapStyles";


const mapLibraries = ["geometry"];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const JobsInMap = ({
  locationsIsLoading,
  selectedOrganization,
  profile,
  history,
  getOfficesShortList,
  officesShortList,
  getOfficeDetails,
  officeDetails,
  orgMembersShortList,
  getOrganizationMembersShortList,
  getJobList,
  jobList = [],
  isLoadingJobs,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const showShadow = useScrollShadow(60);
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";

  // Simple filter state
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedMember, setSelectedMember] = useState("");
  const [selectedOffice, setSelectedOffice] = useState({ value: "", label: "(All Teams)" });
  const [activeTab, setActiveTab] = useState("all");
  const [map, setMap] = useState(null);
  const [jobMarkers, setJobMarkers] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);

  // Extract query params on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryParamDate = queryParams.get("date");
    const queryParamMember = queryParams.get("member");

    if (queryParamDate) {
      setSelectedDate(moment(queryParamDate));
    }

    if (queryParamMember) {
      try {
        const memberData = JSON.parse(decodeURIComponent(queryParamMember));
        setSelectedMember(memberData);
      } catch (e) {
        console.error("Invalid member param:", e);
      }
    }

    // Clean URL after extracting params
    window.history.replaceState(null, "", location.pathname);
  }, []);

  const { isLoaded, loadError } = useLoadScript({
    id: "jobs-in-map-google-maps-script-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });

  const selectOffice = e => {
    if (e && e.value && selectedOrganization && selectedOrganization.id) {
      const payload = {
        organization_id: selectedOrganization.id,
        office_id: e.value,
      };
      getOfficeDetails(payload);
    }
    setSelectedMember("");
    setSelectedOffice(e);
  };

  const selectMember = person => {
    if (person && person.id) {
      setSelectedMember(person);
    }
  };

  const clearMember = () => {
    setSelectedMember("");
  };

  const singleDateChangeDaily = value => {
    if (value === "next") {
      const nextDate = moment(selectedDate).add(1, "day");
      setSelectedDate(nextDate);
    } else if (value === "prev") {
      const prevDate = moment(selectedDate).subtract(1, "day");
      setSelectedDate(prevDate);
    } else if (moment(value).isValid()) {
      // Handle direct date selection from DatePicker
      setSelectedDate(moment(value));
    }
  };

  const selectToday = () => {
    setSelectedDate(moment());
  };

  // Fetch jobs based on filters
  const callGetJobsList = () => {
    const payload = {
      paginate: false,
      start_date: moment(selectedDate).format("YYYY-MM-DD"),
      end_date: moment(selectedDate).format("YYYY-MM-DD"),
    };

    if (selectedMember && selectedMember.id) {
      payload.assignee_id = selectedMember.id;
    }
    if (activeTab && activeTab !== "all") {
      payload.status = activeTab === "missed" ? "missed" : activeTab; 
    }

    if (selectedOrganization && selectedOrganization.id) {
      payload.organization_id = selectedOrganization.id;
      getJobList(payload);
    }
  };

  // Fetch jobs when filters change
  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      callGetJobsList();
    }
  }, [selectedDate, selectedMember, selectedOrganization, getJobList, activeTab]);

  // Filter jobs by status and location
  const filterJobsByStatus = (jobs) => {
    if (activeTab === "all") return jobs;
    
    const statusMap = {
      scheduled: "scheduled",
      in_progress: "in_progress",
      completed: "completed",
      missed: "missed",
    };
    
    const targetStatus = statusMap[activeTab];
    return jobs.filter(job => job.status && job.status.toLowerCase() === targetStatus);
  };

  // Filter jobs to only include those with location and matching status
  const jobsWithLocation = (jobList || [])
    .filter(job => job.location && job.location.latitude && job.location.longitude)
    .filter(job => filterJobsByStatus([job]).length > 0);

  // Initialize selectedJobs as empty (no jobs selected initially)
  useEffect(() => {
    setSelectedJobs([]);
  }, [jobList]);

  // Update markers to show all jobs with location (not filtered by selection)
  useEffect(() => {
    if (jobsWithLocation && jobsWithLocation.length > 0) {
      const markers = jobsWithLocation.map(job => ({
        id: job.id,
        location: {
          lat: job.location.latitude,
          lng: job.location.longitude,
        },
        title: job.name || job.job_code,
        job: job,
      }));
      setJobMarkers(markers);
    } else {
      setJobMarkers([]);
    }
  }, [jobsWithLocation.length, jobList]);

  // Handle job toggle from JobListInMapContainer (single selection only)
  const handleJobToggle = (jobId) => {
    setSelectedJobs(prev => {
      const isCurrentlySelected = prev.includes(jobId);
      
      if (isCurrentlySelected) {
        // Deselect the job
        return [];
      } else {
        // Select the job (replacing any previous selection)
        const selectedJob = jobsWithLocation.find(job => job.id === jobId);
        if (selectedJob && map) {
          // Pan to the marker location
          map.panTo({
            lat: selectedJob.location.latitude,
            lng: selectedJob.location.longitude,
          });
          
          // Find and trigger marker click
          const markerToSelect = jobMarkers.find(marker => marker.id === jobId);
          if (markerToSelect) {
            // Use a small delay to ensure map has panned
            setTimeout(() => {
              // Trigger the marker selection by passing jobId to map component
              setMap(prevMap => {
                if (prevMap && prevMap.markerClickHandler) {
                  prevMap.markerClickHandler(markerToSelect);
                }
                return prevMap;
              });
            }, 300);
          }
        }
        
        // Return only this job (single selection)
        return [jobId];
      }
    });
  };

  // Get organization timezone for time display
  const orgTimezone = selectedOrganization?.timezone || moment.tz.guess();

  return (
    <div className="content">
      <ContainerNew>
        <PageTitle>{t(isOperationType ? "tasks_in_map" : "jobs_in_map")}</PageTitle>
        <StickyFilterContainer margin="0" paddingLeft="0" paddingRight="0" showShadow={showShadow}>
          {/* <CommonFlex
            justifyContent="space-between"
            margin="0px"
            flexFlow="row-wrap"
            
          > */}
            <JobsInMapFilter
              selectedOrganization={selectedOrganization}
              getOrganizationMembersList={getOrganizationMembersShortList}
              organizationMembersList={orgMembersShortList}
              selectedMember={selectedMember}
              selectMember={selectMember}
              clearMember={clearMember}
              selectedDate={selectedDate}
              singleDateChangeDaily={singleDateChangeDaily}
              selectToday={selectToday}
              getOfficesShortList={getOfficesShortList}
              officesShortList={officesShortList}
              selectedOffice={selectedOffice}
              selectOffice={selectOffice}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          {/* </CommonFlex> */}
        </StickyFilterContainer>

        {loadError && (
          <MapAndAlertContainer>
            <MapTopAlert variant="danger">
              {t("error_loading_maps") || "Error loading Google Maps. Please try again later."}
            </MapTopAlert>
          </MapAndAlertContainer>
        )}

        {!isLoaded && !loadError && (
          <ComponentCircleLoader />
        )}

        {isLoaded && !loadError && (
          <CommonGrid columns="minmax(20px, 180px) 1fr" gap="16px">
            <JobListInMapContainer
              jobList={jobsWithLocation}
              isLoadingJobs={isLoadingJobs}
              selectedJobs={selectedJobs}
              onJobToggle={handleJobToggle}
              isOperationType={isOperationType}
            />
            <JobsInGoogleMap
              map={map}
              setMap={setMap}
              jobMarkers={jobMarkers}
              isLoadingJobs={isLoadingJobs}
              orgTimezone={orgTimezone}
              selectedJobs={selectedJobs}
              jobList={jobsWithLocation}
            />
          </CommonGrid>
        )}
           
      </ContainerNew>
    </div>
  );
};

export default JobsInMap;